import { useState } from "react";
import { Link } from "react-router-dom";
import ContactSection, { type ContactPrefill } from "../../components/ContactSection";
import Icon from "../../components/icons";
import PageHero from "../../components/PageHero";
import ProofSection from "../../components/ProofSection";
import Reveal from "../../components/Reveal";
import RetainerSelector, { type RetainerSelection } from "../../components/RetainerSelector";
import SiteLayout from "../../components/SiteLayout";

const engagements = [
  {
    label: "Define",
    title: "Discovery sprint",
    description: "For an important idea that needs sharper definition before a larger investment.",
    outcome: "A tested direction and practical delivery plan",
    items: ["Stakeholder workshop", "User and workflow mapping", "Prototype or technical proof", "Prioritized roadmap"],
  },
  {
    label: "Build",
    title: "Product delivery",
    description: "For a new digital product or meaningful release that needs a dedicated team.",
    outcome: "A working, launch-ready product increment",
    items: ["Product and UX design", "Web or mobile engineering", "Integrations and infrastructure", "Quality and launch support"],
    featured: true,
  },
  {
    label: "Evolve",
    title: "Product partnership",
    description: "For a live product that needs dependable progress, care, and technical leadership.",
    outcome: "A steady rhythm of improvements and support",
    items: ["Roadmap guidance", "Feature development", "Reliability and performance", "Flexible monthly capacity"],
  },
];

const PricingComponent = () => {
  const [contactPrefill, setContactPrefill] = useState<ContactPrefill>();

  const chooseRetainer = (selection: RetainerSelection) => {
    setContactPrefill({
      projectType: selection.projectType,
      budget: "Under $10,000",
      message: `I'm interested in the 20-hour CMJ retainer with a primary focus on ${selection.label.toLowerCase()}. Please help me confirm the best first priorities and start date.`,
    });
    window.requestAnimationFrame(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }));
  };

  return (
    <SiteLayout>
    <PageHero
      eyebrow="Pricing & engagement"
      title={<>Clear scope. <span>Right-sized investment.</span></>}
      description="Custom software should not come with mystery math. We define the outcome, risks, and level of effort together before asking you to commit."
    >
      <div className="hero-actions">
        <a className="button" href="#retainer">Explore the 20-hour retainer <Icon name="arrow" /></a>
        <a className="button button-secondary" href="#a-la-carte">Explore à la carte <Icon name="arrow" /></a>
      </div>
    </PageHero>

    <RetainerSelector onChoose={chooseRetainer} onChooseService={(service) => {
      setContactPrefill({ projectType: service, budget: "", message: "" });
      window.requestAnimationFrame(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
        document.getElementById("contact-name")?.focus({ preventScroll: true });
      });
    }} />

    <section className="light-section pricing-section">
      <div className="site-container">
        <Reveal className="section-heading-row">
          <div><p className="section-kicker">Other ways to work together</p><h2>Choose the shape, then size the work.</h2></div>
          <p>If the right path is not obvious, start with a conversation. We will recommend the smallest engagement that can reduce uncertainty.</p>
        </Reveal>
        <div className="engagement-grid">
          {engagements.map((engagement, index) => (
            <Reveal key={engagement.label} delay={index * 80}>
              <article className={`engagement-card${engagement.featured ? " is-featured" : ""}`}>
                <div className="engagement-top">
                  <span>{engagement.label}</span>
                  {engagement.featured && <em>Most common</em>}
                </div>
                <h3>{engagement.title}</h3>
                <p>{engagement.description}</p>
                <div className="outcome-block"><small>Typical outcome</small><strong>{engagement.outcome}</strong></div>
                <ul>{engagement.items.map((item) => <li key={item}><Icon name="check" />{item}</li>)}</ul>
                <Link className={engagement.featured ? "button" : "button button-dark-outline"} to="/#contact">Discuss this option <Icon name="arrow" /></Link>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="dark-section pricing-notes">
      <div className="site-container pricing-note-grid">
        <Reveal><p className="section-kicker">What shapes a quote</p><h2>No black box.</h2></Reveal>
        <div className="quote-factors">
          {[["01", "Product scope", "The number and depth of workflows we need to design and build."], ["02", "Technical context", "Integrations, legacy systems, data, security, and platform requirements."], ["03", "Delivery pace", "Deadlines, parallel workstreams, and the amount of uncertainty to resolve."], ["04", "Team involvement", "Where your team wants CMJ to lead, collaborate, or augment existing capability."]].map(([number, title, copy], index) => (
            <Reveal key={number} delay={index * 60} className="quote-factor"><span>{number}</span><div><h3>{title}</h3><p>{copy}</p></div></Reveal>
          ))}
        </div>
      </div>
    </section>

    <ProofSection />

    <ContactSection prefill={contactPrefill} />
  </SiteLayout>
  );
};

export default PricingComponent;
